import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Sparkles, Heart, Briefcase, Activity, 
  Lock, UserPlus, ChevronRight, Star, Eye, EyeOff,
  MessageCircle, History, Zap
} from 'lucide-react';
import { useAuth } from '@/components/AuthContext';

const GUEST_SCAN_KEY = 'reveliae_guest_scan';

const THEME_ICONS = {
  love: Heart,
  career: Briefcase,
  health: Activity,
};

const THEME_NAMES = {
  love: 'Amour',
  career: 'Carrière',
  health: 'Santé',
};

const THEME_COLORS = {
  love: 'from-pink-500 to-rose-500',
  career: 'from-blue-500 to-indigo-500',
  health: 'from-green-500 to-emerald-500',
};

const GuestResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [reading, setReading] = useState(location.state?.reading || null);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [revealedInsights, setRevealedInsights] = useState(0);

  useEffect(() => {
    if (!reading) {
      // Try to load from localStorage
      const storedScan = localStorage.getItem(GUEST_SCAN_KEY);
      if (storedScan) {
        const data = JSON.parse(storedScan);
        setReading({ id: data.readingId, insights: data.insights });
      } else {
        navigate('/try');
      }
    }
  }, [reading, navigate]);

  // Show signup modal after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isAuthenticated) {
        setShowSignupModal(true);
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, [isAuthenticated]);

  // Gradually reveal insights
  useEffect(() => {
    if (reading?.insights) {
      const timer = setInterval(() => {
        setRevealedInsights(prev => {
          if (prev < reading.insights.length) return prev + 1;
          return prev;
        });
      }, 800);
      return () => clearInterval(timer);
    }
  }, [reading]);

  if (!reading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const visibleInsights = reading.insights?.slice(0, 3) || []; // Show only 3 themes (80%)
  const lockedInsights = 4; // Pretend there are more locked

  const handleCreateAccount = () => {
    navigate('/auth', { 
      state: { 
        pendingGuestScan: localStorage.getItem(GUEST_SCAN_KEY),
        from: { pathname: '/history' }
      } 
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4 rounded-full text-gray-600 hover:bg-emerald-100"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center"
            >
              <Sparkles className="h-10 w-10 text-white" />
            </motion.div>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Votre lecture gratuite
            </h1>
            
            <p className="text-gray-600">
              Découvrez ce que révèle votre paume droite
            </p>
          </div>
        </motion.div>

        {/* Results */}
        <div className="space-y-4">
          {visibleInsights.map((insight, index) => {
            const Icon = THEME_ICONS[insight.theme] || Star;
            const isRevealed = index < revealedInsights;
            
            return (
              <motion.div
                key={insight.theme}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: isRevealed ? 1 : 0.3, x: isRevealed ? 0 : -20 }}
                transition={{ delay: index * 0.2, duration: 0.5 }}
                className="bg-white rounded-2xl border border-emerald-200 shadow-lg overflow-hidden"
              >
                {/* Theme Header */}
                <div className={`p-4 bg-gradient-to-r ${THEME_COLORS[insight.theme] || 'from-gray-500 to-gray-600'}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">
                        {THEME_NAMES[insight.theme] || insight.theme}
                      </h3>
                      <p className="text-white/80 text-sm">
                        {insight.title}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-5 space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-emerald-700 mb-2 flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      Observation
                    </h4>
                    <p className="text-gray-700">{insight.observation}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold text-emerald-700 mb-2">
                      Interprétation
                    </h4>
                    <p className="text-gray-700">{insight.interpretation}</p>
                  </div>
                  
                  {/* Advice - Blurred for guests */}
                  <div className="relative">
                    <h4 className="text-sm font-semibold text-emerald-700 mb-2 flex items-center gap-2">
                      <Zap className="h-4 w-4" />
                      Conseil personnalisé
                    </h4>
                    <div className="relative">
                      <p className="text-gray-700 blur-sm select-none">
                        {insight.advice || "Votre conseil personnalisé sera révélé après inscription..."}
                      </p>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="px-3 py-1 bg-amber-100 text-amber-700 text-sm rounded-full flex items-center gap-1">
                          <Lock className="h-3 w-3" />
                          Créez un compte pour voir
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}

          {/* Locked Insights Preview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl border border-gray-300 p-6"
          >
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-300 flex items-center justify-center">
                <Lock className="h-8 w-8 text-gray-500" />
              </div>
              
              <h3 className="text-lg font-bold text-gray-700 mb-2">
                +{lockedInsights} analyses supplémentaires
              </h3>
              
              <p className="text-gray-600 text-sm mb-4">
                Finances, Longévité, Créativité, Spiritualité...
              </p>
              
              <div className="flex flex-wrap justify-center gap-2">
                {['💰 Finances', '⏳ Longévité', '🎨 Créativité', '✨ Spiritualité'].map((item) => (
                  <span
                    key={item}
                    className="px-3 py-1 bg-white/50 rounded-full text-sm text-gray-600"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="mt-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl p-6 text-white text-center"
        >
          <h3 className="text-xl font-bold mb-2">
            Sauvegardez votre lecture
          </h3>
          
          <p className="text-white/90 mb-6">
            Créez un compte gratuit pour sauvegarder cette analyse, débloquer tous les thèmes et accéder à des fonctionnalités exclusives.
          </p>
          
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[
              { icon: History, label: 'Historique' },
              { icon: MessageCircle, label: 'Chat IA' },
              { icon: Star, label: 'Favoris' }
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="bg-white/10 rounded-xl p-3">
                <Icon className="h-6 w-6 mx-auto mb-1" />
                <span className="text-xs">{label}</span>
              </div>
            ))}
          </div>
          
          <Button
            onClick={handleCreateAccount}
            className="w-full py-6 rounded-xl bg-white text-emerald-600 hover:bg-gray-100 text-lg font-semibold"
            data-testid="signup-cta-btn"
          >
            <UserPlus className="mr-2 h-5 w-5" />
            Créer mon compte gratuit
            <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
          
          <p className="text-white/70 text-sm mt-4">
            Inscription gratuite • Pas de carte bancaire requise
          </p>
        </motion.div>
      </div>

      {/* Signup Modal */}
      <AnimatePresence>
        {showSignupModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={() => setShowSignupModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                  <Sparkles className="h-10 w-10 text-white" />
                </div>
                
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Votre lecture vous a plu ?
                </h2>
                
                <p className="text-gray-600 mb-6">
                  Créez un compte pour <strong>sauvegarder</strong> cette analyse et <strong>historiser</strong> vos futures lectures.
                </p>
                
                <div className="space-y-3">
                  <Button
                    onClick={handleCreateAccount}
                    className="w-full py-5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white text-lg"
                    data-testid="modal-signup-btn"
                  >
                    <UserPlus className="mr-2 h-5 w-5" />
                    Oui, sauvegarder ma lecture
                  </Button>
                  
                  <button
                    onClick={() => setShowSignupModal(false)}
                    className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Continuer sans compte
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GuestResultsPage;
